import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api';

export default function AdminCreditsPanel() {
  const { t } = useTranslation();
  const [balances, setBalances]   = useState([]);
  const [orgList, setOrgList]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [grantForm, setGrantForm] = useState({ orgId: '', credits: '', reason: '' });
  const [discountForm, setDiscountForm] = useState({ orgId: '', bonusCredits: '', reason: '' });
  const [grantResult, setGrantResult]   = useState(null);
  const [discountResult, setDiscountResult] = useState(null);
  const [grantError, setGrantError]         = useState(null);
  const [discountError, setDiscountError]   = useState(null);
  const [granting, setGranting]             = useState(false);
  const [discounting, setDiscounting]       = useState(false);

  const loadBalances = useCallback(() => {
    setLoading(true);
    axios.get('/iasm/admin/credits')
      .then(r => setBalances(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadBalances();
    axios.get('/iasm/admin/orgs').then(r => setOrgList(r.data)).catch(() => {});
  }, [loadBalances]);

  const submitGrant = () => {
    if (!grantForm.orgId || !grantForm.credits) return;
    setGranting(true);
    setGrantError(null);
    setGrantResult(null);
    axios.post('/iasm/admin/credits/grant', {
      orgId: Number(grantForm.orgId),
      credits: Number(grantForm.credits),
      reason: grantForm.reason || 'Admin grant',
    })
      .then(r => {
        setGrantResult(t('AdminCreditsPanel.grantSuccess', { credits: grantForm.credits, balance: r.data.credits }));
        setGrantForm({ orgId: '', credits: '', reason: '' });
        loadBalances();
      })
      .catch(e => setGrantError(e?.response?.data?.message || t('AdminCreditsPanel.grantFailed')))
      .finally(() => setGranting(false));
  };

  const submitDiscount = () => {
    if (!discountForm.orgId || !discountForm.bonusCredits) return;
    setDiscounting(true);
    setDiscountError(null);
    setDiscountResult(null);
    axios.post('/iasm/admin/credits/discount', {
      orgId: Number(discountForm.orgId),
      bonusCredits: Number(discountForm.bonusCredits),
      reason: discountForm.reason || 'Admin discount',
    })
      .then(r => {
        setDiscountResult(t('AdminCreditsPanel.discountSuccess', { credits: discountForm.bonusCredits, balance: r.data.credits }));
        setDiscountForm({ orgId: '', bonusCredits: '', reason: '' });
        loadBalances();
      })
      .catch(e => setDiscountError(e?.response?.data?.message || t('AdminCreditsPanel.discountFailed')))
      .finally(() => setDiscounting(false));
  };

  const creditColor = (n) => n >= 50 ? '#15803d' : n >= 10 ? '#d97706' : n > 0 ? '#ea580c' : '#dc2626';

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '28px' }}>

        {/* Grant credits */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 600, color: '#111827' }}>
            <i className="fas fa-gift" style={{ marginRight: '8px', color: '#2563eb' }} />{t('AdminCreditsPanel.grantCreditsHeading')}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <FormField label={t('AdminCreditsPanel.organisationLabel')}>
              <select value={grantForm.orgId} onChange={e => setGrantForm(p => ({ ...p, orgId: e.target.value }))}
                style={selectStyle}>
                <option value="">{t('AdminCreditsPanel.selectOrganisationPlaceholder')}</option>
                {orgList.map(o => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
              </select>
            </FormField>
            <FormField label={t('AdminCreditsPanel.creditsToGrantLabel')}>
              <input type="number" min="1" value={grantForm.credits}
                onChange={e => setGrantForm(p => ({ ...p, credits: e.target.value }))}
                placeholder={t('AdminCreditsPanel.creditsToGrantPlaceholder')} style={inputStyle} />
            </FormField>
            <FormField label={t('AdminCreditsPanel.reasonOptionalLabel')}>
              <input value={grantForm.reason}
                onChange={e => setGrantForm(p => ({ ...p, reason: e.target.value }))}
                placeholder={t('AdminCreditsPanel.grantReasonPlaceholder')} style={inputStyle} />
            </FormField>
            {grantError && <ErrMsg msg={grantError} />}
            {grantResult && <OkMsg msg={grantResult} />}
            <button onClick={submitGrant} disabled={granting || !grantForm.orgId || !grantForm.credits}
              style={{ ...btnStyle, background: grantForm.orgId && grantForm.credits ? '#2563eb' : '#e5e7eb', color: grantForm.orgId && grantForm.credits ? '#fff' : '#9ca3af' }}>
              {granting ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: '5px' }} />{t('AdminCreditsPanel.grantingButton')}</> : t('AdminCreditsPanel.grantCreditsButton')}
            </button>
          </div>
        </div>

        {/* Apply discount */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 600, color: '#111827' }}>
            <i className="fas fa-tag" style={{ marginRight: '8px', color: '#7c3aed' }} />{t('AdminCreditsPanel.applyDiscountHeading')}
          </h3>
          <p style={{ margin: '0 0 14px', fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>
            {t('AdminCreditsPanel.bonusCreditsDescription')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <FormField label={t('AdminCreditsPanel.organisationLabel')}>
              <select value={discountForm.orgId} onChange={e => setDiscountForm(p => ({ ...p, orgId: e.target.value }))}
                style={selectStyle}>
                <option value="">{t('AdminCreditsPanel.selectOrganisationPlaceholder')}</option>
                {orgList.map(o => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
              </select>
            </FormField>
            <FormField label={t('AdminCreditsPanel.bonusCreditsLabel')}>
              <input type="number" min="1" value={discountForm.bonusCredits}
                onChange={e => setDiscountForm(p => ({ ...p, bonusCredits: e.target.value }))}
                placeholder={t('AdminCreditsPanel.bonusCreditsPlaceholder')} style={inputStyle} />
            </FormField>
            <FormField label={t('AdminCreditsPanel.reasonOptionalLabel')}>
              <input value={discountForm.reason}
                onChange={e => setDiscountForm(p => ({ ...p, reason: e.target.value }))}
                placeholder={t('AdminCreditsPanel.discountReasonPlaceholder')} style={inputStyle} />
            </FormField>
            {discountError && <ErrMsg msg={discountError} />}
            {discountResult && <OkMsg msg={discountResult} />}
            <button onClick={submitDiscount} disabled={discounting || !discountForm.orgId || !discountForm.bonusCredits}
              style={{ ...btnStyle, background: discountForm.orgId && discountForm.bonusCredits ? '#7c3aed' : '#e5e7eb', color: discountForm.orgId && discountForm.bonusCredits ? '#fff' : '#9ca3af' }}>
              {discounting ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: '5px' }} />{t('AdminCreditsPanel.applyingButton')}</> : t('AdminCreditsPanel.applyDiscountButton')}
            </button>
          </div>
        </div>
      </div>

      {/* All balances table */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#111827' }}>
          <i className="fas fa-list-alt" style={{ marginRight: '8px', color: '#6b7280' }} />{t('AdminCreditsPanel.allBalancesHeading')}
        </h3>
        <button onClick={loadBalances} style={{ padding: '5px 12px', border: '1px solid #d1d5db', background: '#fff', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
          <i className="fas fa-sync" style={{ marginRight: '4px' }} />{t('AdminCreditsPanel.refreshButton')}
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} />{t('AdminCreditsPanel.loading')}
        </div>
      ) : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {[t('AdminCreditsPanel.colOrganisation'), t('AdminCreditsPanel.colCredits'), t('AdminCreditsPanel.colLastUpdated')].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {balances.map(b => (
                <tr key={b.orgId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 500, color: '#111827' }}>{b.orgName}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ fontWeight: 800, fontSize: '16px', color: creditColor(b.credits) }}>
                      {b.credits}
                    </span>
                    {b.credits === 0 && (
                      <span style={{ marginLeft: '8px', fontSize: '11px', color: '#dc2626', background: '#fee2e2', padding: '1px 6px', borderRadius: '4px' }}>
                        {t('AdminCreditsPanel.noCreditsBadge')}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px', color: '#6b7280', fontSize: '12px' }}>
                    {b.updatedAt ? new Date(b.updatedAt).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
              {balances.length === 0 && (
                <tr><td colSpan={3} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                  {t('AdminCreditsPanel.emptyState')}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  );
}

function ErrMsg({ msg }) {
  return (
    <div style={{ padding: '8px 10px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '12px', color: '#991b1b' }}>
      <i className="fas fa-times-circle" style={{ marginRight: '5px' }} />{msg}
    </div>
  );
}

function OkMsg({ msg }) {
  return (
    <div style={{ padding: '8px 10px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', fontSize: '12px', color: '#15803d' }}>
      <i className="fas fa-check-circle" style={{ marginRight: '5px' }} />{msg}
    </div>
  );
}

const inputStyle  = { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' };
const selectStyle = { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', background: '#fff' };
const btnStyle    = { padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 };
